import Accordion from '@theme/components/utils/Accordion'
import AppButton from '@theme/components/atom/AppButton'
import CategoryBox from '@theme/components/category/CategoryBox'
import DetectMobileMixin from '~/mixins/DetectMobileMixin'
import FileMixin from '~/mixins/FileMixin'
import PageProductDetail from '~/themes/base/components/product/PageProductDetail'
import ProductCartAssistant from '~/themes/oknadverenamiru/components/product/ProductCartAssistant'
const ProductsVisited = () => import('@theme/components/product/ProductsVisited/ProductsVisitedLazy')
import { get, isEmpty, set } from 'lodash'
import { hydrateWhenVisible } from 'vue-lazy-hydration'
import UniqueSellingPoints from '@theme/components/shop/UniqueSellingPoints'
import eventDefinitions from '../../../../../events/eventDefinitions'
import gql from 'graphql-tag'

const toPositiveNumber = value => {
  const number = Number(value)

  return Number.isFinite(number) && number > 0 ? number : null
}

const parseProductSlug = slug => {
  const value = slug || ''
  const dashDimensions = value.match(/^(.*)-(\d+)-(\d+)$/)
  const compactDimensions = value.match(/^(.*)-(\d+)x(\d+)$/)
  const match = dashDimensions || compactDimensions

  if (!match) {
    return {
      baseSlug: value,
      width: null,
      height: null,
    }
  }

  return {
    baseSlug: match[1],
    width: toPositiveNumber(match[2]),
    height: toPositiveNumber(match[3]),
  }
}

const hasDashDimensions = slug => /-\d+-\d+$/.test(slug || '')

export default {
  extends: PageProductDetail,
  components: {
    AppButton,
    CategoryBox,
    Accordion,
    UniqueSellingPoints,
    ProductsVisited: hydrateWhenVisible(ProductsVisited),
    ProductCartAssistant,
  },
  mixins: [DetectMobileMixin, FileMixin],
  props: {
    routeConfiguration: Object,
  },
  data() {
    return {
      issueFormInModal: null,
      configurationWeight: null,
      sizes: { height: 0, width: 0 },
      isProductConfiguratorSticky: true,
      isStickyConfiguratorActive: false,
      previousPrice: null,
      isProductPriceReady: true,
      isInitialPriceSame: null,
      productParent: null,
      loadingParent: false,
      accordions: [],
      stickyHeight: 0,
      local: {
        accessoryProducts: null,
        alternativeProducts: null,
        comments: null,
        productReviews: null,
        relatedCategories: null,
        relatedProducts: null,
      },
      cartValidity: {
        isValid: true,
        pending: false,
        reason: null,
      },
    }
  },
  computed: {
    configuratorTargetId(vm) {
      let id = 'bottom'

      if (vm.isStickyConfiguratorActive && !vm.isMobileDetected) {
        id = 'top'
      } else if (vm.isStickyConfiguratorActive && vm.isMobileDetected) {
        id = 'middle'
      }

      return [id, vm.product.id].join('-')
    },
    assemblyFile() {
      const files = this.product.content.assembly_manual || []
      return files?.[0]?.fileKey
    },
    productWeight() {
      return this.configurationWeight || this.product?.attributes?.weight || this.product?.variants?.[0]?.weight
    },
    productMainImage() {
      return this.product?.mainImage
    },
    priceWithTax() {
      if (!this.displayedVariant?.prices?.unitWithTax) return null

      const basePrice = this.displayedVariant.prices.unitWithTax
      const additional = this.colourConfiguration?.originalWithTax || 0

      return this.isProductPriceReady ? basePrice : basePrice + additional
    },
    priceWithoutTax() {
      if (!this.displayedVariant?.prices?.unitWithoutTax) return null

      const basePrice = this.displayedVariant.prices.unitWithoutTax
      const additional = this.colourConfiguration?.originalWithoutTax || 0

      return this.isProductPriceReady ? basePrice : basePrice + additional
    },
    colourConfiguration() {
      return this.product?.attributes?.configuration?.find(config => config.code === this.$route.query.barva)
    },
    hasConfigurator() {
      return get(this.product, 'attributes.configuration.length') || false
    },
    isAvailable() {
      return this.product?.availability?.isAvailable && this.product?.availability?.stock > 0
    },
    anchorText() {
      return this.isAvailable
        ? this.$t('PageProductDetail.chooseAccessories')
        : this.$t('PageProductDetail.modifyProductAnchor')
    },
    cartDisabledExternal() {
      return this.cartValidity.pending || !this.cartValidity.isValid
    },
  },
  created() {
    this.ensureDisplayedVariant()
    if (process.client) {
      localStorage.setItem('lastSentProductId', '')
    }
  },
  async mounted() {
    this.$nuxt.$on('productConfigValidity', this.onProductConfigValidity)

    this.setInitialSizes()
    this.sendViewEventProduct()
    this.previousPrice = get(this.displayedVariant, 'prices.unitWithTax', null)

    if (!this.productParent && this.shouldLoadParent()) {
      await this.loadProductParent()
    }

    if (this.sizes.width && this.sizes.height) {
      this.onConfigurationChange({ sizes: this.sizes })
    }

    window.addEventListener('scroll', this.handleScroll)

    const { state, dispatch } = this.$store
    const product = this.product
    const query = this.$route.query

    const ts = this.$themeSettings || {}
    const comps = ts.components || {}
    const pd = comps.PageProductDetail || {}

    const accessoriesEnabled = !!(pd.accessoryProducts?.enable && product?.accessories?.length > 0)
    const alternativesEnabled = !!(
      (pd.alternativeProducts?.enable || pd.showColorVariants) &&
      product?.alternatives?.length > 0
    )
    const commentsEnabled = !!(comps.ProductComments?.enable && pd.showProductComments)
    const reviewsEnabled = !!(state.globals?.settings?.functions?.products_user_reviews && pd.reviews?.enable)
    const relatedCatsEnabled = !!(pd.showRelatedCategories && product?.categoryIds?.length > 0)

    const page = query && !isNaN(query.r) ? Number(query.r) + 1 : 1
    const onlyWithReview = pd.reviews?.onlyWithReview ? 1 : 0
    const perPage = pd.reviews?.initialCount ?? 0

    try {
      const [
        accessoryProducts,
        alternativeProducts,
        comments,
        productReviews,
        relatedCategories,
        relatedProducts,
      ] = await Promise.all([
        accessoriesEnabled ? dispatch('product/LOAD_PRODUCTS', { ids: product.accessories }) : Promise.resolve([]),
        alternativesEnabled ? dispatch('product/LOAD_PRODUCTS', { ids: product.alternatives }) : Promise.resolve([]),
        commentsEnabled ? dispatch('product/LOAD_PRODUCT_COMMENTS', { productId: product.id }) : Promise.resolve([]),
        reviewsEnabled
          ? dispatch('product/LOAD_REVIEWS', {
              productId: product.id,
              page,
              perPage,
              onlyWithReview,
            })
          : Promise.resolve({}),
        relatedCatsEnabled ? dispatch('category/LOAD_CATEGORIES', { ids: product.categoryIds }) : Promise.resolve([]),
        product?.related?.length > 0
          ? dispatch('product/LOAD_PRODUCTS', { ids: product.related })
          : Promise.resolve([]),
      ])

      this.$set(this.local, 'accessoryProducts', accessoryProducts)
      this.$set(this.local, 'alternativeProducts', alternativeProducts)
      this.$set(this.local, 'comments', comments)
      this.$set(this.local, 'productReviews', productReviews)
      this.$set(this.local, 'relatedCategories', relatedCategories)
      this.$set(this.local, 'relatedProducts', relatedProducts)
    } catch (e) {
      console.warn('Secondary data load failed:', e)
    }
    this.$nextTick(() => {
      this.measureSticky()
      this.updateStickyStop()
      window.addEventListener('scroll', this.updateStickyStop, { passive: true })
      window.addEventListener('resize', this.onResize, { passive: true })
    })
  },
  beforeDestroy() {
    this.$nuxt.$off('productConfigValidity', this.onProductConfigValidity)

    window.removeEventListener('scroll', this.updateStickyStop)
    window.removeEventListener('resize', this.onResize)
    window.removeEventListener('scroll', this.handleScroll)
  },
  watch: {
    '$route.params.slug'(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.loadProductParent()
      }
    },
    displayedVariant: {
      deep: true,
      handler(variant) {
        const newPrice = get(variant, 'prices.unitWithTax')
        if (this.previousPrice !== null) {
          this.isInitialPriceSame = newPrice === this.previousPrice
          this.isProductPriceReady = newPrice !== this.previousPrice
        }
        this.previousPrice = newPrice
      },
    },
  },
  methods: {
    handleScroll() {
      this.isProductConfiguratorSticky = window.scrollY > 100
    },
    addToCart() {
      this.$refs?.ProductConfiguratorBox?.addToCart()
    },
    ensureDisplayedVariant() {
      if (!this.product) {
        return
      }

      if (!Array.isArray(this.product.variants)) {
        this.$set(this.product, 'variants', [])
      }

      const fallbackVariant =
        this.displayedVariant ||
        this.selectedVariant ||
        this.product.variants[0] ||
        (this.product.prices ? this.product : null)

      if (!fallbackVariant) {
        return
      }

      if (!this.displayedVariant) {
        this.$set(this, 'displayedVariant', fallbackVariant)
      }
      if (!this.selectedVariant) {
        this.$set(this, 'selectedVariant', fallbackVariant)
      }
    },
    setInitialSizes() {
      const slugDimensions = parseProductSlug(this.$route?.params?.slug)
      const height =
        toPositiveNumber(get(this.routeConfiguration, 'height')) ||
        slugDimensions.height ||
        toPositiveNumber(get(this.product, 'attributes.height')) ||
        0
      const width =
        toPositiveNumber(get(this.routeConfiguration, 'width')) ||
        slugDimensions.width ||
        toPositiveNumber(get(this.product, 'attributes.width')) ||
        0
      this.$set(this, 'sizes', { height, width })
      set(this.product, 'attributes.height', height)
      set(this.product, 'attributes.width', width)
      this.$set(this, 'product', this.product)
      this.onConfigurationChange({ sizes: this.sizes })
    },
    shouldLoadParent() {
      const slug = this.$route?.params?.slug || this.product?.slug || ''
      const { baseSlug } = parseProductSlug(slug)

      return hasDashDimensions(slug) && Boolean(baseSlug && baseSlug !== slug)
    },
    onConfigurationChange({ sizes }) {
      this.$set(this, 'sizes', sizes)
      const { width, height } = sizes

      let prices = get(this.product, 'content.product_configurations_priced[0].product_configuration_value_prices', [])

      if (!prices.length && this.productParent) {
        prices = get(
          this.productParent,
          'content.product_configurations_priced[0].product_configuration_value_prices',
          [],
        )
      }

      if (!prices.length) {
        this.configurationWeight = null
        return
      }

      const priceVariant = prices.filter(item => item.max_width >= width && item.max_height >= height).sort((a, b) => {
        const diffA = a.max_width - width + (a.max_height - height)
        const diffB = b.max_width - width + (b.max_height - height)
        return diffA - diffB
      })[0]

      if (!priceVariant) {
        const closestVariant = prices.reduce((closest, current) => {
          const closestDiff = Math.abs(closest.max_width - width) + Math.abs(closest.max_height - height)
          const currentDiff = Math.abs(current.max_width - width) + Math.abs(current.max_height - height)
          return currentDiff < closestDiff ? current : closest
        })
        this.configurationWeight = closestVariant ? closestVariant.weight : null
      } else {
        this.configurationWeight = priceVariant.weight
      }
    },
    openModal() {
      import('@theme/components/shop/IssueFormInModal').then(component => {
        this.issueFormInModal = component.default
        this.$nextTick(() => {
          this.$refs.issueFormInModal.open()
        })
      })
    },
    sendViewEventProduct() {
      const currentProductId = this.product?.id
      if (!currentProductId) return

      const lastSentId = localStorage.getItem('lastSentProductId')

      if (String(lastSentId) === String(currentProductId)) return
      localStorage.setItem('lastSentProductId', currentProductId)

      const colourCode = this.$route?.query?.barva
      const colourId = this.resolveColourId(colourCode)

      this.$eventBus.$emit(eventDefinitions.PRODUCT.PAGE_VIEW, {
        product: this.product,
        variant: this.displayedVariant,
        colour: colourId,
        price: this.priceWithoutTax,
        priceVat: this.priceWithTax,
      })
    },
    resolveColourId(code) {
      const COLOR_MAP = {
        bila_bila: '1',
        zlaty_dub_zlaty_dub: '2',
        orech_orech: '3',
        anthrazitgrau_struktura_bila: '4',
      }
      return COLOR_MAP[code] || null
    },
    async loadProductParent() {
      if (!hasDashDimensions(this.$route?.params?.slug)) {
        return
      }

      console.log('loadProductParent', this.$route?.params?.slug)
      const { baseSlug } = parseProductSlug(this.$route?.params?.slug)
      if (!baseSlug) return

      this.loadingParent = true
      try {
        const parent = await this.$store.dispatch('product/LOAD_PRODUCT', { slug: baseSlug })
        if (!parent || parent.error) {
          return
        }

        this.$set(this, 'productParent', parent)

        if (!this.product.variants?.length && parent.variants?.length) {
          this.$set(this.product, 'variants', parent.variants)
          this.ensureDisplayedVariant()
        }

        if (parent?.content?.accordion_reference?.length) {
          const ids = parent.content.accordion_reference.map(i => i.entityId)
          const { data } = await this.$apolloProvider.defaultClient.query({
            query: gql`
              query Accordion($ids: [String]) {
                accordion(filters: { _id: $ids }) {
                  entities {
                    _id
                    title
                    content
                    main_image {
                      path
                      alt
                    }
                  }
                }
              }
            `,
            variables: { ids },
          })

          this.accordions = data.accordion.entities
        }
      } catch (e) {
        console.warn('Parent product load failed:', e)
      } finally {
        this.loadingParent = false
      }
    },
    onResize() {
      this.measureSticky()
      this.updateStickyStop()
    },
    measureSticky() {
      if (!this.$el || typeof this.$el.querySelector !== 'function') {
        return
      }

      const stickyEl = this.$el.querySelector('.Sticky__element--active, .Sticky__element--sticky')
      this.stickyHeight = stickyEl ? stickyEl.offsetHeight : 0
    },
    updateStickyStop() {
      if (!this.$el || typeof this.$el.querySelector !== 'function') {
        return
      }

      const content = this.$el.querySelector('.ProductDetail__content')
      const boundary = this.$refs['ProductDetail--configuratorStickyBoundary']

      if (!content || !boundary) return

      const contentRect = content.getBoundingClientRect()
      const pageScroll = window.scrollY || window.pageYOffset
      const pageTop = pageScroll + contentRect.top
      const contentBottom = pageTop + contentRect.height
      const viewportBottom = (window.scrollY || window.pageYOffset) + window.innerHeight
      const stopLimit = contentBottom - this.stickyHeight
      const shouldStop = viewportBottom >= stopLimit

      boundary.classList.toggle('is-stopped', shouldStop)
    },
    onProductConfigValidity({ isValid, pending, reason }) {
      this.cartValidity.isValid = !!isValid
      this.cartValidity.pending = !!pending
      this.cartValidity.reason = reason || null
    },
  },
}
